import { Link } from "react-router-dom";
import { Button, Checkbox, ConfirmationDialog, DropArea, TableHeader, TableRow } from "../../components";
import { useContext, useState } from "react";
import { FileTypes, headersType, ToastVariants } from "../../models";
import Back from "../../assets/images/back.svg";
import { formatAndValidateOHIP, onFileError, validateEmail } from "../../utils";
import Papa from "papaparse";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import moment from "moment";
import { PatientType } from "../../models/patient.model";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { inviteBulkOfficePatient } from "../../redux/slices/PatientSlice";

const requiredColumns = ["Email", "DOB"];
const nameColumns = ["Firstname", "Lastname"];

const bulkPatientsHeaders: headersType[] = [
  {
    title: "First Name",
    extraClasses: "w-full pl-0 truncate",
    sortKey: "firstName",
    isClickable: true,
  },
  {
    title: "Last Name",
    extraClasses: "w-full pl-0 truncate",
    sortKey: "lastName",
    isClickable: true,
  },
  {
    title: "Date of Birth",
    extraClasses: "w-full hidden lg:block",
    sortKey: "",
    isClickable: false,
  },
  {
    title: "Phone Number",
    extraClasses: "w-full hidden lg:block",
    sortKey: "number",
    isClickable: false,
  },
  {
    title: "Email",
    extraClasses: "w-full hidden lg:block truncate",
    sortKey: "email",
    isClickable: true,
  },
  {
    title: "",
    extraClasses: "w-full hidden lg:block",
    sortKey: "",
    isClickable: false,
  },
];

const PatientBulkInvite = ({ officeId, setInviteBulkPatient }: { officeId: string; setInviteBulkPatient: (val: boolean) => void }) => {
  const dispath = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const [bulkPatients, setBulkPatients] = useState(false);
  const [dropAreaError, setDropAreaError] = useState<string>("");
  const [patientList, setPatientList] = useState<PatientType[]>([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [bulkPatientsExtraClasses] = useState<string[]>(bulkPatientsHeaders.map(head => head.extraClasses));
  const [loading, setLoading] = useState(false);

  function handleFileUpload(file: File) {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) => {
        const { data, meta } = results;
        // Check for missing columns
        // Check for missing required columns
        const missingColumns = requiredColumns.filter(col => !meta.fields.includes(col));

        // Check that at least one of "First Name" or "Surname" columns exists
        const hasAtLeastOneNameColumn = nameColumns.some(col => meta.fields.includes(col));

        if (missingColumns.length || !hasAtLeastOneNameColumn) {
          toast?.errorToast("Invalid .csv format. Please reach out to a Docnote team member for assistance.");
          return;
        }
        const isValidDate = (dob: string): boolean => {
          if (!/^\d{2}-\d{2}-\d{4}$/.test(dob)) return false;

          const [day, month, year] = dob.split("-").map(Number);
          const date = new Date(year, month - 1, day);

          return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
        };
        if (!data.length) {
          toast?.errorToast("Invalid .csv format. Please reach out to a Docnote team member for assistance.");
          return;
        }
        // Handle filteredData, e.g., set to state or display in table
        const list = data.map((item: any) => {
          const ohip = formatAndValidateOHIP(item["Mobile number"]);
          return {
            firstName: item["Firstname"],
            lastName: item["Lastname"],
            email: item["Email"],
            dob: item["DOB"],
            ohip: ohip.valid ? ohip.formatted : "",
            phone: item["Mobile number"],
            action: true,
          };
        });
        setPatientList(list);
        setBulkPatients(true);
      },
    });
  }

  const handleChange = (index: number, value: boolean) => {
    const item = patientList[index];
    setPatientList([...patientList.slice(0, index), { ...item, action: value }, ...patientList.slice(index + 1)]);
  };

  const handleCancel = (val?: boolean) => {
    setBulkPatients(false);
    setInviteBulkPatient(val ?? false);
  };

  const handleInvite = async () => {
    setLoading(true);
    const body = patientList
      .filter(item => item.action)
      .map(item => {
        return {
          officeId: officeId,
          email: item.email,
          firstName: item.firstName?.trim()?.length ? item.firstName?.trim() : undefined,
          lastName: item.lastName?.trim().length ? item.lastName.trim() : undefined,
          dateOfBirth: item.dob,
          phoneNumber: item.ohip?.trim().length ? item.ohip.replace(/[^0-9]/g, "").slice(-10) : undefined,
        };
      });
    const res = await dispath(
      inviteBulkOfficePatient({
        officeId: officeId,
        body,
      })
    );
    if (res.payload) {
      toast?.openToast(
        "Bulk invitation was successful. You will receive a email that summarizes the invites you sent out.",
        2000,
        ToastVariants.Success
      );
      handleCancel(true);
    } else {
      toast?.errorToast("Bulk invitation failed");
    }
    setLoading(false);
  };
  const sampleFile = process.env.REACT_APP_PATIENT_SAMPLE_FILE;

  return (
    <>
      <Link
        to={"#"}
        onClick={() => (bulkPatients ? setShowCancelDialog(true) : handleCancel())}
        className="text-docsigna-orange font-bold flex gap-2 mb-6"
      >
        <img src={Back} alt="Back" className="w-3" /> Back to patients page
      </Link>
      <h2 className="uppercase text-lg font-bold mb-2">Bulk Patient invite</h2>
      {!bulkPatients ? (
        <>
          <p className="mb-2">
            Please upload your patient CSV file. Docnote will automatically filter your list to include only patients over 18 years of age whose email
            address is not currently associated with an existing patient account.
          </p>
          <p className="mb-6">Before proceeding, kindly ensure that your CSV file does not include any inactive or deceased patients.</p>
          <p className="text-base mb-3 text-docsigna-blue-dark">
            Download the{" "}
            <a
              href={sampleFile}
              target="_blank"
              download
              className="text-red-500 underline hover:text-docsigna-blue-dark cursor-pointer"
              rel="noreferrer"
            >
              sample file
            </a>{" "}
            to view the required format and fields.
          </p>
          <div className="py-4 w-[500px] bg-docsigna-pink-light rounded-md">
            <DropArea
              allowedFileTypes={[FileTypes.JPEG, FileTypes.PNG, FileTypes.PDF]}
              handleFileChange={file => handleFileUpload(file)}
              onFilesChange={() => null}
              isLocalUpload={true}
              dropAreaText="CSV"
              onError={err => setDropAreaError(onFileError(err))}
              maxFileSize={50 * 1024 * 1024}
              maxNumOfFiles={10}
              filePreviewVisible={true}
              showButton={true}
            />
          </div>
        </>
      ) : (
        <>
          <p className="mb-6">
            The following patients who are over 18 years of age and have a valid email address were successfully uploaded to your Office. Please
            select all the patients for whom you would like to send a personalized invitation to set up an account with your office on Docnote.
          </p>
          <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
            {bulkPatientsHeaders.map((head, index) => (
              <>
                <TableHeader
                  title={head.title}
                  index={index}
                  key={index}
                  sortIcon={null}
                  handleSort={() => null}
                  extraClassesHeader={head.extraClasses}
                  isClickable={head.isClickable}
                />
              </>
            ))}
          </div>
          {patientList.map((rowData, index) => {
            const data = {
              firstName: rowData.firstName ? rowData.firstName : "-",
              lastName: rowData.lastName ? rowData.lastName : "-",
              // dob: moment(rowData.dob, "DD-MM-YYYY").format("ll"),
              dob: rowData.dob,
              phone: rowData.phone,
              email: rowData.email,
              ohip: (
                <div
                  key={index}
                  className="flex justify-between items-center float-right
"
                >
                  {/* <p>{rowData.ohip ? rowData.ohip : "-"}</p> */}
                  <Checkbox label="" checked={rowData.action} onChange={e => handleChange(index, e.target.checked)} />
                </div>
              ),
            };
            return (
              <TableRow
                data={Object.values(data)}
                key={`${index.toString()}-${rowData.email}`}
                onRowClick={() => {}}
                tooltipVisible={false}
                handleOpenTooltip={() => null}
                handleCloseTooltip={() => null}
                extraClassesRow={bulkPatientsExtraClasses}
                handleTooltipClick={() => null}
              />
            );
          })}
          <div className="w-full flex justify-end gap-2 mt-6">
            <Button text="Cancel" disabled={loading} onClick={() => setShowCancelDialog(true)} className="text-docsigna-orange bg-white" />
            <Button text="Send Invites" onClick={handleInvite} variant={loading ? "loading" : "Primary"} />
          </div>
        </>
      )}
      <ConfirmationDialog
        open={showCancelDialog}
        title={`Are you sure you want to leave?`}
        description={"If you exit, your patient csv data will be lost"}
        successButtonText={"Exit"}
        handleSuccess={handleCancel}
        failureButtonText={"Cancel"}
        handleFailure={() => setShowCancelDialog(false)}
      />
    </>
  );
};

export default PatientBulkInvite;
